import { storeToRefs } from "pinia";
import { h } from "vue";
import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";

import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import KiliApi from "@/data/api/kili/kili-api";
import { ERouteName, ROUTES_WITHOUT_SIDE_MENU } from "@/router/router.type";
import {
  newSectionManagementFeatureFlag,
  siteDefaultProgramFeatureFlag,
} from "@/services/feature-flags/env-feature-flags";
import { mixpanelPageViews } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { useCurrentUserStore } from "@/stores/current-user/current-user-store";

import Activation from "@/app/authentication/activation.vue";
import Login from "@/app/authentication/login.vue";
import PasswordRecovery from "@/app/authentication/password-recovery.vue";
import Piloting from "@/app/control/piloting.vue";
import SiteSectionsPlanning from "@/app/control/program-planning/site-sections-planning.vue";
import NewProgrammingList from "@/app/control/programming/new-programming-list.vue";
import ProgrammingDetails from "@/app/control/programming/programming-details.vue";
import ProgrammingList from "@/app/control/programming/programming-list.vue";
import Programming from "@/app/control/programming/programming-sites.vue";
import ConsumptionMonitoring from "@/app/data/consumption-monitoring/consumption-monitoring.vue";
import SiteConsumptionMonitoring from "@/app/data/consumption-monitoring/site-consumption-monitoring.vue";
import DashboardView from "@/app/data/dashboard/dashboard-view.vue";
import NewSectionManagement from "@/app/tools/new-section-management/new-section-management.vue";
import SiteSectionsTree from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.vue";
import SectionManagementSites from "@/app/tools/section-management/section-management-sites.vue";
import SiteSections from "@/app/tools/section-management/site-sections/site-sections.vue";
import RouterPageWithChildren from "@/components/layout/router-page-with-children.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: ERouteName.HOME,
    redirect: { name: ERouteName.LOGIN },
  },
  {
    path: "/login",
    name: ERouteName.LOGIN,
    component: Login,
  },
  {
    path: "/activation",
    name: ERouteName.ACTIVATION,
    component: Activation,
  },
  {
    path: "/password-recovery",
    name: ERouteName.PASSWORD_RECOVERY,
    component: PasswordRecovery,
  },
  {
    path: "/piloting",
    name: ERouteName.PILOTING,
    component: h(RouterPageWithChildren, { component: Piloting, name: ERouteName.PILOTING }),
    children: [
      {
        path: ":siteId/planning",
        name: ERouteName.PLANNING,
        component: SiteSectionsPlanning,
        meta: {
          envFeatureFlag: newSectionManagementFeatureFlag,
        },
      },
    ],
  },
  {
    path: "/programming",
    name: ERouteName.PROGRAMMING,
    component: h(RouterPageWithChildren, { component: Programming, name: ERouteName.PROGRAMMING }),
    children: [
      {
        path: ":siteId/programs",
        name: ERouteName.PROGRAMMING_LIST_NEW,
        component: h(RouterPageWithChildren, { component: NewProgrammingList, name: ERouteName.PROGRAMMING_LIST_NEW }),
        children: [
          {
            path: "new",
            name: ERouteName.PROGRAMMING_CREATION_NEW,
            component: () => import("@/app/control/programming/programming-details.vue"),
            props: { creationMode: true },
          },
          {
            path: ":programId",
            name: ERouteName.PROGRAMMING_DETAILS_NEW,
            component: () => import("@/app/control/programming/programming-details.vue"),
          },
        ],
        meta: {
          envFeatureFlag: siteDefaultProgramFeatureFlag,
        },
      },
      {
        path: ":siteId/programs-legacy",
        name: ERouteName.PROGRAMMING_LIST,
        component: h(RouterPageWithChildren, { component: ProgrammingList, name: ERouteName.PROGRAMMING_LIST }),
        children: [
          {
            path: "new",
            name: ERouteName.PROGRAMMING_CREATION,
            component: ProgrammingDetails,
            props: { creationMode: true },
          },
          {
            path: ":programId",
            name: ERouteName.PROGRAMMING_DETAILS,
            component: ProgrammingDetails,
          },
        ],
      },
    ],
  },
  {
    path: "/dashboard",
    name: ERouteName.DASHBOARD_VIEW,
    component: DashboardView,
  },
  {
    path: "/consumption-monitoring",
    name: ERouteName.CONSUMPTION_MONITORING,
    component: h(RouterPageWithChildren, { component: ConsumptionMonitoring, name: ERouteName.CONSUMPTION_MONITORING }),
    children: [
      {
        path: ":siteId",
        name: ERouteName.CONSUMPTION_MONITORING_SITE_DETAILS,
        component: SiteConsumptionMonitoring,
      },
    ],
  },
  {
    path: "/section-management-legacy",
    name: ERouteName.SECTION_MANAGEMENT,
    component: h(RouterPageWithChildren, { component: SectionManagementSites, name: ERouteName.SECTION_MANAGEMENT }),
    children: [
      {
        path: ":siteId/sections",
        name: ERouteName.SITE_SECTIONS,
        component: SiteSections,
      },
    ],
  },
  {
    path: "/section-management",
    name: ERouteName.SECTION_MANAGEMENT_NEW,
    component: h(RouterPageWithChildren, { component: NewSectionManagement, name: ERouteName.SECTION_MANAGEMENT_NEW }),
    children: [
      {
        path: ":siteId/sections-tree",
        name: ERouteName.SITE_SECTIONS_TREE,
        component: SiteSectionsTree,
      },
    ],
    meta: {
      envFeatureFlag: newSectionManagementFeatureFlag,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (guard) => {
  const currentUserStore = useCurrentUserStore();
  const { currentUser } = storeToRefs(currentUserStore);
  const { isEnvFeatureEnabled } = useFeatureFlag();

  if (!currentUser.value) {
    try {
      const currentUser = await KiliApi.getCurrentUser();
      await currentUserStore.login(currentUser);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      if (ROUTES_WITHOUT_SIDE_MENU.includes(guard.name as ERouteName)) {
        return;
      }

      return { name: ERouteName.LOGIN };
    }
  }

  if (!currentUser.value) {
    return { name: ERouteName.LOGIN };
  }

  if (guard.name === ERouteName.LOGIN) {
    return { name: ERouteName.PILOTING };
  }

  if (!guard.meta.envFeatureFlag) {
    return;
  }

  if (isEnvFeatureEnabled(guard.meta.envFeatureFlag)) {
    return;
  }

  const canUserBypass = guard.meta.envFeatureFlag.userBypass?.(currentUser.value);
  if (!canUserBypass) {
    return { name: ERouteName.PILOTING };
  }
});

router.afterEach((guard) => {
  const maybeMixpanelPageViewEvent = mixpanelPageViews[guard.name as ERouteName];

  if (maybeMixpanelPageViewEvent) {
    MixpanelService.trackEvent(maybeMixpanelPageViewEvent as string);
  }
});

export default router;
